import React, { Component } from "react";
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid as Container,
  Modal,
  Row,
  Table
} from "react-bootstrap";

import Card from "components/Card/Card.jsx";
import ViewportAlert from "components/ViewportAlert/ViewportAlert";
import Chart from "../../components/Charts/chart";
import {
  formatAlarmNumber,
  getAlarmSeverity,
  getTankState
} from "utils/alarmPresentation";
import {
  DEFAULT_DETECTION_PARAMETERS,
  detectCollections,
  findNearestTemperature
} from "utils/collectionDetection";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://hgsystem.pl/mangusta";

const DEFAULT_INPUTS = [
  {
    input_id: 1,
    enabled: 1,
    label: "Mleko",
    unit: "l",
    function_type: 3
  },
  {
    input_id: 2,
    enabled: 1,
    label: "Temperatura",
    unit: "°C",
    function_type: 1
  },
  {
    input_id: 3,
    enabled: 0,
    label: "Wejście analogowe 3",
    unit: "",
    function_type: 0
  },
  {
    input_id: 4,
    enabled: 0,
    label: "Wejście analogowe 4",
    unit: "",
    function_type: 0
  }
];

const CHART_COLORS = ["#1DC7EA", "#FB404B", "#87CB16", "#FFA534"];

function getInitialDeviceId(props, devices) {
  const requestedId =
    props.location &&
    new URLSearchParams(props.location.search || "").get("device");
  const requestedDevice = devices.find(
    device => String(device.id) === String(requestedId)
  );
  if (requestedDevice) return String(requestedDevice.id);
  return devices[0] ? String(devices[0].id) : "";
}

// AI: below section was generated by an AI
function getInitialAlarmContext(props) {
  const parameters = new URLSearchParams(
    (props.location && props.location.search) || ""
  );
  const from = Number(parameters.get("from"));
  const to = Number(parameters.get("to"));
  const hasRange =
    Number.isFinite(from) &&
    Number.isFinite(to) &&
    from > 0 &&
    to > from;
  return {
    alarmId: parameters.get("alarm") || "",
    from: hasRange ? from : null,
    to: hasRange ? to : null
  };
}

function alarmSeriesIds(series) {
  const preferred = series
    .filter(
      definition =>
        definition.functionType === 1 ||
        definition.functionType === 2 ||
        definition.functionType === 3
    )
    .map(definition => definition.id);
  return preferred.length > 0
    ? preferred
    : series.map(definition => definition.id);
}
// AI: end

function getAvailableSeries(props, deviceId) {
  const settings =
    (props.deviceSettings || {})[String(deviceId)] || {};
  const inputs = Array.isArray(settings.analog_inputs)
    ? settings.analog_inputs
    : DEFAULT_INPUTS;
  return inputs
    .filter(input => Number(input.enabled) === 1)
    .map(input => {
      const id = Number(input.input_id);
      const label = input.label || `Wejście analogowe ${id}`;
      const functionType =
        input.function_type === null ||
        input.function_type === undefined
          ? id === 1
            ? 3
            : id === 2
              ? 1
              : 0
          : Number(input.function_type) || 0;
      return {
        id,
        label,
        axisTitle: label,
        // Temperature function takes precedence over a stale saved unit.
        unit: functionType === 1 ? "°C" : input.unit || "",
        functionType,
        color: CHART_COLORS[(id - 1) % CHART_COLORS.length]
      };
    });
}

const TIME_RANGES = [
  { value: 3600, label: "1 godzina" },
  { value: 86400, label: "1 dzień" },
  { value: 172800, label: "2 dni" },
  { value: 604800, label: "Tydzień" },
  { value: 1209600, label: "2 tygodnie" },
  { value: 2419200, label: "Miesiąc" }
];

function localDateTimeValue(date) {
  const pad = value => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

class Charts extends Component {
  alarmRequestId = 0;

  constructor(props) {
    super(props);
    const devices = Array.isArray(props.device) ? props.device : [];
    const firstDeviceId = getInitialDeviceId(props, devices);
    const firstSeries = getAvailableSeries(props, firstDeviceId);
    const alarmContext = getInitialAlarmContext(props);
    const firstSeriesIds = alarmContext.alarmId
      ? alarmSeriesIds(firstSeries)
      : firstSeries.map(series => series.id);
    const firstCollectionSeries = firstSeries.find(
      series => series.functionType === 2 || series.functionType === 3
    );
    const now = new Date();
    const defaultCustomFrom = localDateTimeValue(
      alarmContext.from
        ? new Date(alarmContext.from * 1000)
        : new Date(now.getTime() - 172800000)
    );
    const defaultCustomTo = localDateTimeValue(
      alarmContext.to ? new Date(alarmContext.to * 1000) : now
    );
    this.state = {
      showModal: false,
      validationError: "",
      activeDevice: firstDeviceId,
      activeSeriesIds: firstSeriesIds,
      activeTimeRange: 172800,
      activeRangeMode: alarmContext.from ? "custom" : "preset",
      activeCustomFrom: defaultCustomFrom,
      activeCustomTo: defaultCustomTo,
      activeVisualization: "line",
      activeShowPoints: true,
      draftDevice: firstDeviceId,
      draftSeriesIds: firstSeriesIds,
      draftTimeRange: 172800,
      draftRangeMode: alarmContext.from ? "custom" : "preset",
      draftCustomFrom: defaultCustomFrom,
      draftCustomTo: defaultCustomTo,
      draftVisualization: "line",
      draftShowPoints: true,
      sharedTemperatureAxis: false,
      chartRefreshKey: 0,
      chartLoading: false,
      chartData: {},
      alarms: [],
      alarmLoading: false,
      alarmError: "",
      alarmLimitReached: false,
      focusedAlarmId: alarmContext.alarmId,
      collections: [],
      candidates: [],
      collectionLoading: false,
      pdfExporting: false,
      collectionError: "",
      collectionMessage: "",
      editingCollection: null,
      manualCollection: {
        inputId: firstCollectionSeries ? String(firstCollectionSeries.id) : "",
        startedAt: localDateTimeValue(new Date()),
        amount: "",
        temperature: "",
        note: ""
      },
      detectionParameters: { ...DEFAULT_DETECTION_PARAMETERS }
    };
  }

  componentDidMount() {
    this.loadCollections();
    this.loadAlarms();
  }

  componentDidUpdate(previousProps, previousState) {
    const previousDevices = Array.isArray(previousProps.device)
      ? previousProps.device
      : [];
    const devices = Array.isArray(this.props.device) ? this.props.device : [];
    if (previousDevices.length === 0 && devices.length > 0) {
      const firstDeviceId = getInitialDeviceId(this.props, devices);
      const availableIds = getAvailableSeries(
        this.props,
        firstDeviceId
      );
      const selectedIds = this.state.focusedAlarmId
        ? alarmSeriesIds(availableIds)
        : availableIds.map(series => series.id);
      this.setState({
        activeDevice: firstDeviceId,
        draftDevice: firstDeviceId,
        activeSeriesIds: selectedIds,
        draftSeriesIds: selectedIds
      });
      return;
    }

    if (previousProps.deviceSettings !== this.props.deviceSettings) {
      const availableIds = getAvailableSeries(
        this.props,
        this.state.activeDevice
      ).map(series => series.id);
      const selectedIds = this.state.activeSeriesIds.filter(
        id => availableIds.indexOf(id) >= 0
      );
      this.setState({
        activeSeriesIds:
          selectedIds.length > 0 ? selectedIds : availableIds,
        draftSeriesIds:
          selectedIds.length > 0 ? selectedIds : availableIds
      });
    }

    if (
      previousState.activeDevice !== this.state.activeDevice ||
      previousState.activeTimeRange !== this.state.activeTimeRange ||
      previousState.activeRangeMode !== this.state.activeRangeMode ||
      previousState.activeCustomFrom !== this.state.activeCustomFrom ||
      previousState.activeCustomTo !== this.state.activeCustomTo
    ) {
      this.setState({ chartData: {}, candidates: [] });
      this.loadCollections();
      this.loadAlarms();
    }
  }

  openSettings = () => {
    this.setState({
      showModal: true,
      validationError: "",
      draftDevice: this.state.activeDevice,
      draftSeriesIds: this.state.activeSeriesIds.slice(),
      draftTimeRange: this.state.activeTimeRange,
      draftRangeMode: this.state.activeRangeMode,
      draftCustomFrom: this.state.activeCustomFrom,
      draftCustomTo: this.state.activeCustomTo,
      draftVisualization: this.state.activeVisualization,
      draftShowPoints: this.state.activeShowPoints
    });
  };

  toggleDraftSeries = seriesId => {
    this.setState(previousState => {
      const selected = previousState.draftSeriesIds.slice();
      const index = selected.indexOf(seriesId);
      if (index >= 0)
        selected.splice(index, 1);
      else
        selected.push(seriesId);
      return {
        draftSeriesIds: selected,
        validationError: ""
      };
    });
  };

  changeDraftDevice = deviceId => {
    const availableIds = getAvailableSeries(this.props, deviceId).map(
      series => series.id
    );
    this.setState({
      draftDevice: deviceId,
      draftSeriesIds: availableIds,
      validationError: ""
    });
  };

  applySettings = () => {
    if (!this.state.draftDevice) {
      this.setState({ validationError: "Wybierz urządzenie." });
      return;
    }
    if (this.state.draftSeriesIds.length === 0) {
      this.setState({
        validationError: "Wybierz co najmniej jeden rodzaj danych."
      });
      return;
    }
    if (this.state.draftRangeMode === "custom") {
      const from = new Date(this.state.draftCustomFrom).getTime();
      const to = new Date(this.state.draftCustomTo).getTime();
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
        this.setState({
          validationError:
            "Wybierz prawidłowy początek i koniec własnego zakresu."
        });
        return;
      }
      if (to - from > 5 * 365 * 86400000) {
        this.setState({
          validationError: "Własny zakres nie może przekraczać 5 lat."
        });
        return;
      }
    }

    if (this.props.history) {
      const parameters = new URLSearchParams({
        device: String(this.state.draftDevice)
      });
      if (this.state.draftRangeMode === "custom") {
        parameters.set(
          "from",
          String(
            Math.floor(
              new Date(this.state.draftCustomFrom).getTime() / 1000
            )
          )
        );
        parameters.set(
          "to",
          String(
            Math.floor(new Date(this.state.draftCustomTo).getTime() / 1000)
          )
        );
      }
      this.props.history.replace(`/charts?${parameters.toString()}`);
    }

    this.setState({
      showModal: false,
      validationError: "",
      activeDevice: this.state.draftDevice,
      activeSeriesIds: this.state.draftSeriesIds.slice(),
      activeTimeRange: this.state.draftTimeRange,
      activeRangeMode: this.state.draftRangeMode,
      activeCustomFrom: this.state.draftCustomFrom,
      activeCustomTo: this.state.draftCustomTo,
      activeVisualization: this.state.draftVisualization,
      activeShowPoints: this.state.draftShowPoints,
      focusedAlarmId: ""
    });
  };

  collectionPost = body => {
    if (!this.props.credentials)
      return Promise.reject(new Error("Brak danych uwierzytelniających."));
    return fetch(`${API_BASE}/collections.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: this.props.credentials,
        ...body
      })
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.error || "Nie udało się wykonać operacji.");
      return data;
    });
  };

  alarmPost = body => {
    if (!this.props.credentials)
      return Promise.reject(new Error("Brak danych uwierzytelniających."));
    return fetch(`${API_BASE}/alarms.php`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        auth: this.props.credentials,
        ...body
      })
    }).then(async response => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(data.error || "Nie udało się pobrać alarmów.");
      return data;
    });
  };

  getCollectionSeries(deviceId) {
    return getAvailableSeries(this.props, deviceId).filter(
      series => series.functionType === 2 || series.functionType === 3
    );
  }

  getTemperatureSeries(deviceId) {
    return getAvailableSeries(this.props, deviceId).find(
      series => series.functionType === 1
    );
  }

  getActiveRange() {
    const now = Math.floor(Date.now() / 1000);
    if (this.state.activeRangeMode === "custom") {
      return {
        from: Math.floor(
          new Date(this.state.activeCustomFrom).getTime() / 1000
        ),
        to: Math.min(
          Math.floor(new Date(this.state.activeCustomTo).getTime() / 1000),
          now + 21600
        )
      };
    }
    return {
      from: now - this.state.activeTimeRange,
      to: now + 21600
    };
  }

  loadCollections = () => {
    if (!this.state.activeDevice || !this.props.credentials) return;
    const range = this.getActiveRange();
    this.setState({ collectionLoading: true, collectionError: "" });
    this.collectionPost({
      action: "list",
      device_id: this.state.activeDevice,
      from: range.from,
      to: range.to
    })
      .then(data => {
        const collections = Array.isArray(data.collections)
          ? data.collections
          : [];
        const available = this.getCollectionSeries(this.state.activeDevice);
        this.setState(previousState => ({
          collections,
          collectionLoading: false,
          manualCollection: {
            ...previousState.manualCollection,
            inputId:
              previousState.manualCollection.inputId &&
              available.some(
                series =>
                  String(series.id) ===
                  String(previousState.manualCollection.inputId)
              )
                ? previousState.manualCollection.inputId
                : available[0]
                  ? String(available[0].id)
                  : ""
          }
        }));
      })
      .catch(error =>
        this.setState({
          collectionLoading: false,
          collectionError: error.message
        })
      );
  };

  // Loads all alarm occurrences raised inside the visible chart range.
  loadAlarms = () => {
    if (!this.state.activeDevice || !this.props.credentials) return;
    const range = this.getActiveRange();
    const maximumServerTimestamp =
      Math.floor(Date.now() / 1000) + 6 * 60 * 60 - 60;
    const requestId = ++this.alarmRequestId;
    this.setState({
      alarmLoading: true,
      alarmError: "",
      alarmLimitReached: false
    });
    this.alarmPost({
      action: "list",
      status: "all",
      device_id: this.state.activeDevice,
      from: range.from,
      to: Math.min(range.to, maximumServerTimestamp),
      limit: 500,
      offset: 0
    })
      .then(data => {
        if (requestId !== this.alarmRequestId) return;
        const alarms = Array.isArray(data.alarms) ? data.alarms : [];
        alarms.sort(
          (left, right) =>
            Number(left.raised_at) - Number(right.raised_at)
        );
        this.setState({
          alarms,
          alarmLoading: false,
          alarmLimitReached: alarms.length >= 500
        });
      })
      .catch(error => {
        if (requestId !== this.alarmRequestId) return;
        this.setState({
          alarms: [],
          alarmLoading: false,
          alarmError: error.message
        });
      });
  };

  handleChartDataLoaded = chartData => {
    this.setState({ chartData: chartData || {} });
  };

  // Reloads every server-backed layer associated with the visible chart.
  refreshChartData = () => {
    this.setState(
      previousState => ({
        chartData: {},
        candidates: [],
        chartRefreshKey: previousState.chartRefreshKey + 1
      }),
      () => {
        this.loadCollections();
        this.loadAlarms();
      }
    );
  };

  updateDetectionParameter = (field, value) => {
    this.setState(previousState => ({
      detectionParameters: {
        ...previousState.detectionParameters,
        [field]: Number(value)
      },
      candidates: [],
      collectionMessage: ""
    }));
  };

  analyzeCollections = () => {
    const collectionSeries = this.getCollectionSeries(
      this.state.activeDevice
    ).filter(
      series => this.state.activeSeriesIds.indexOf(series.id) >= 0
    );
    if (collectionSeries.length === 0) {
      this.setState({
        collectionError:
          "Dodaj do wykresu wejście z funkcją pomiaru poziomu medium."
      });
      return;
    }

    const temperatureSeries = this.getTemperatureSeries(
      this.state.activeDevice
    );
    const temperatureData = temperatureSeries
      ? this.state.chartData[String(temperatureSeries.id)] || []
      : [];
    let candidates = [];
    collectionSeries.forEach(series => {
      const detected = detectCollections(
        this.state.chartData[String(series.id)] || [],
        this.state.detectionParameters
      ).map(candidate => ({
        ...candidate,
        key: `${series.id}-${candidate.key}`,
        inputId: series.id,
        inputLabel: series.label,
        temperatureInputId: temperatureSeries
          ? temperatureSeries.id
          : null,
        temperature: findNearestTemperature(
          temperatureData,
          candidate.startedAt
        )
      }));
      candidates = candidates.concat(detected);
    });

    candidates = candidates.filter(
      candidate =>
        !this.state.collections.some(
          collection =>
            Number(collection.input_id) === Number(candidate.inputId) &&
            Math.abs(
              Number(collection.started_at) - candidate.startedAt
            ) <= 900
        )
    );
    candidates.sort((a, b) => b.startedAt - a.startedAt);
    this.setState({
      candidates,
      collectionError: "",
      collectionMessage:
        candidates.length > 0
          ? `Wykryto ${candidates.length} potencjalnych odbiorów.`
          : "Nie wykryto odbiorów dla podanych parametrów."
    });
  };

  updateManualCollection = (field, value) => {
    this.setState(previousState => ({
      manualCollection: {
        ...previousState.manualCollection,
        [field]: value
      },
      collectionError: "",
      collectionMessage: ""
    }));
  };

  saveManualCollection = () => {
    const form = this.state.manualCollection;
    const startedAt = Math.floor(new Date(form.startedAt).getTime() / 1000);
    const amount = Number(form.amount);
    if (
      !form.inputId ||
      !Number.isFinite(startedAt) ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      this.setState({
        collectionError: "Wybierz wejście, datę oraz prawidłową ilość."
      });
      return;
    }

    this.setState({ collectionLoading: true, collectionError: "" });
    this.collectionPost({
      action: "create",
      device_id: this.state.activeDevice,
      input_id: Number(form.inputId),
      started_at: startedAt,
      amount,
      temperature:
        form.temperature === "" ? null : Number(form.temperature),
      source: "manual",
      note: form.note
    })
      .then(() => {
        this.setState(previousState => ({
          collectionLoading: false,
          collectionMessage: "Odbiór został zapisany.",
          manualCollection: {
            ...previousState.manualCollection,
            startedAt: localDateTimeValue(new Date()),
            amount: "",
            temperature: "",
            note: ""
          }
        }));
        this.loadCollections();
      })
      .catch(error =>
        this.setState({
          collectionLoading: false,
          collectionError: error.message
        })
      );
  };

  saveDetectedCollection = candidate => {
    this.setState({ collectionLoading: true, collectionError: "" });
    this.collectionPost({
      action: "create",
      device_id: this.state.activeDevice,
      input_id: candidate.inputId,
      started_at: candidate.startedAt,
      ended_at: candidate.endedAt,
      amount: candidate.amount,
      temperature: candidate.temperature,
      temperature_input_id: candidate.temperatureInputId,
      start_value: candidate.startValue,
      end_value: candidate.endValue,
      source: "detected",
      detection_parameters: this.state.detectionParameters
    })
      .then(() => {
        this.setState(previousState => ({
          collectionLoading: false,
          candidates: previousState.candidates.filter(
            item => item.key !== candidate.key
          ),
          collectionMessage: "Wykryty odbiór został zapisany."
        }));
        this.loadCollections();
      })
      .catch(error =>
        this.setState({
          collectionLoading: false,
          collectionError: error.message
        })
      );
  };

  deleteCollection = collection => {
    if (!window.confirm("Usunąć zapisany odbiór?")) return;
    this.setState({ collectionLoading: true, collectionError: "" });
    this.collectionPost({
      action: "delete",
      device_id: this.state.activeDevice,
      collection_id: collection.id
    })
      .then(() => {
        this.setState({
          collectionLoading: false,
          collectionMessage: "Odbiór został usunięty."
        });
        this.loadCollections();
      })
      .catch(error =>
        this.setState({
          collectionLoading: false,
          collectionError: error.message
        })
      );
  };

  editCollection = collection => {
    this.setState({
      editingCollection: {
        id: collection.id,
        inputId: String(collection.input_id),
        startedAt: localDateTimeValue(
          new Date(Number(collection.started_at) * 1000)
        ),
        amount: String(collection.amount),
        temperature:
          collection.temperature === null ? "" : String(collection.temperature),
        durationSeconds:
          collection.ended_at === null
            ? null
            : Math.max(
                0,
                Number(collection.ended_at) - Number(collection.started_at)
              ),
        temperatureInputId: collection.temperature_input_id,
        startValue: collection.start_value,
        endValue: collection.end_value,
        note: collection.note || "",
        source: collection.source
      },
      collectionError: "",
      collectionMessage: ""
    });
  };

  updateEditingCollection = (field, value) => {
    this.setState(previousState => ({
      editingCollection: {
        ...previousState.editingCollection,
        [field]: value
      }
    }));
  };

  saveEditingCollection = () => {
    const form = this.state.editingCollection;
    if (!form) return;
    const startedAt = Math.floor(new Date(form.startedAt).getTime() / 1000);
    const amount = Number(form.amount);
    if (
      !form.inputId ||
      !Number.isFinite(startedAt) ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      this.setState({
        collectionError: "Wybierz wejście, datę oraz prawidłową ilość."
      });
      return;
    }

    this.setState({ collectionLoading: true, collectionError: "" });
    this.collectionPost({
      action: "update",
      device_id: this.state.activeDevice,
      collection_id: form.id,
      input_id: Number(form.inputId),
      started_at: startedAt,
      ended_at:
        form.durationSeconds === null
          ? null
          : startedAt + form.durationSeconds,
      amount,
      temperature:
        form.temperature === "" ? null : Number(form.temperature),
      temperature_input_id: form.temperatureInputId,
      start_value: form.startValue,
      end_value: form.endValue,
      source: form.source,
      note: form.note
    })
      .then(() => {
        this.setState({
          collectionLoading: false,
          editingCollection: null,
          collectionMessage: "Odbiór został zaktualizowany."
        });
        this.loadCollections();
      })
      .catch(error =>
        this.setState({
          collectionLoading: false,
          collectionError: error.message
        })
      );
  };

  exportChartPdf = () => {
    if (!this.chartComponent) return;
    this.setState({ pdfExporting: true });
    this.chartComponent
      .exportPdf()
      .catch(error =>
        this.setState({ collectionError: error.message })
      )
      .then(() => this.setState({ pdfExporting: false }));
  };

  formatCollectionDate = timestamp =>
    new Date(Number(timestamp) * 1000).toLocaleString("pl-PL");

  getHighlightRanges() {
    const saved = this.state.collections.map(collection => ({
      start: Number(collection.started_at),
      end:
        Number(collection.ended_at) ||
        Number(collection.started_at) + 600,
      status: "saved",
      label: `Odbiór ${Number(collection.amount).toFixed(0)}`
    }));
    const detected = this.state.candidates.map(candidate => ({
      start: candidate.startedAt,
      end: candidate.endedAt,
      status: "candidate",
      label: `Wykryto ${candidate.amount.toFixed(0)}`,
      amount: candidate.amount,
      actionKey: candidate.key
    }));
    return saved.concat(detected);
  }

  // Converts alarm records into point markers rendered independently from
  // collection ranges, so an alarm refresh does not reset chart zoom.
  getAlarmMarkers() {
    const focusedId = String(this.state.focusedAlarmId || "");
    return this.state.alarms
      .map(alarm => {
        const timestamp = Number(alarm.raised_at);
        if (!Number.isFinite(timestamp)) return null;
        const severity = getAlarmSeverity(alarm.severity);
        const tankState = getTankState(alarm.tank_state);
        const status = alarm.resolved_at ? "Zakończony" : "Aktywny";
        const message =
          alarm.message ||
          alarm.title ||
          `Alarm ${alarm.alarm_code === undefined ? "" : alarm.alarm_code}`;
        const details = [
          `[bold]${severity.label}[/] · ${status}`,
          new Date(timestamp * 1000).toLocaleString("pl-PL"),
          `Stan zbiornika: ${tankState.label}`,
          String(message).replace(/\[/g, "(").replace(/\]/g, ")")
        ];
        let temperature = null;
        let volume = null;
        let diagnostics = alarm.diagnostics;
        if (typeof diagnostics === "string") {
          try {
            diagnostics = JSON.parse(diagnostics);
          } catch (error) {
            diagnostics = null;
          }
        }
        if (diagnostics && typeof diagnostics === "object") {
          const measurements = [];
          if (
            diagnostics.temperature !== undefined &&
            diagnostics.temperature !== null
          ) {
            temperature = `${formatAlarmNumber(
              diagnostics.temperature
            )} °C`;
            measurements.push(
              `Temperatura: ${temperature}`
            );
          }
          if (
            diagnostics.volume !== undefined &&
            diagnostics.volume !== null
          ) {
            volume = `${formatAlarmNumber(diagnostics.volume)} l`;
            measurements.push(
              `Objętość: ${volume}`
            );
          }
          if (measurements.length > 0) details.push(measurements.join(" · "));
        }
        if (alarm.alarm_code !== undefined && alarm.alarm_code !== null)
          details.push(`Kod alarmu: ${alarm.alarm_code}`);
        if (alarm.value !== undefined && alarm.value !== null) {
          let valueLine = `Wartość: ${formatAlarmNumber(alarm.value)}${
            alarm.unit ? ` ${alarm.unit}` : ""
          }`;
          if (
            alarm.threshold_value !== undefined &&
            alarm.threshold_value !== null
          )
            valueLine += ` · próg: ${formatAlarmNumber(
              alarm.threshold_value
            )}`;
          details.push(valueLine);
        }
        if (alarm.occurrence_id)
          details.push(`ID wystąpienia: ${alarm.occurrence_id}`);
        return {
          id: alarm.id,
          timestamp,
          severity: alarm.severity,
          color: severity.color,
          tooltip: details.join("\n"),
          alarmDetails: {
            severityLabel: severity.label,
            status,
            date: new Date(timestamp * 1000).toLocaleString("pl-PL"),
            tankState: tankState.label,
            tankStateColor: tankState.color,
            message: String(message).replace(/\[/g, "(").replace(/\]/g, ")"),
            temperature,
            volume,
            code:
              alarm.alarm_code !== undefined && alarm.alarm_code !== null
                ? alarm.alarm_code
                : null,
            value:
              alarm.value !== undefined && alarm.value !== null
                ? `${formatAlarmNumber(alarm.value)}${
                    alarm.unit ? ` ${alarm.unit}` : ""
                  }`
                : null,
            threshold:
              alarm.threshold_value !== undefined &&
              alarm.threshold_value !== null
                ? `${formatAlarmNumber(alarm.threshold_value)}${
                    alarm.unit ? ` ${alarm.unit}` : ""
                  }`
                : null,
            occurrenceId: alarm.occurrence_id || null
          },
          focused: focusedId !== "" && focusedId === String(alarm.id)
        };
      })
      .filter(Boolean);
  }

  getActiveDeviceName() {
    const devices = Array.isArray(this.props.device) ? this.props.device : [];
    const device = devices.find(
      item => String(item.id) === this.state.activeDevice
    );
    return device ? device.desc || device.id : "Wykres";
  }

  getCategory() {
    const selectedLabels = getAvailableSeries(
      this.props,
      this.state.activeDevice
    ).filter(
      series => this.state.activeSeriesIds.indexOf(series.id) >= 0
    ).map(series => series.label);
    const range = TIME_RANGES.find(
      item => item.value === this.state.activeTimeRange
    );
    const rangeLabel =
      this.state.activeRangeMode === "custom"
        ? `${new Date(this.state.activeCustomFrom).toLocaleString(
            "pl-PL"
          )} – ${new Date(this.state.activeCustomTo).toLocaleString("pl-PL")}`
        : range
          ? range.label
          : "";
    return `${selectedLabels.join(" + ")} · ${
      rangeLabel
    } · ${this.state.activeVisualization === "column" ? "słupki" : "linie"}`;
  }

  renderSettingsModal(devices) {
    const availableSeries = getAvailableSeries(
      this.props,
      this.state.draftDevice
    );
    return (
      <Modal
        show={this.state.showModal}
        onHide={() => this.setState({ showModal: false })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Ustawienia wykresu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.state.validationError && (
            <Alert bsStyle="danger">{this.state.validationError}</Alert>
          )}

          <FormGroup>
            <ControlLabel>Urządzenie</ControlLabel>
            <FormControl
              componentClass="select"
              value={this.state.draftDevice}
              onChange={event => this.changeDraftDevice(event.target.value)}
            >
              {devices.map(device => (
                <option value={String(device.id)} key={device.id}>
                  {device.desc || device.id} ({device.id})
                </option>
              ))}
            </FormControl>
          </FormGroup>

          <FormGroup>
            <ControlLabel>Dane wyświetlane na wykresie</ControlLabel>
            <div
              style={{
                border: "1px solid #e3e3e3",
                borderRadius: 4,
                padding: "8px 12px"
              }}
            >
              {availableSeries.map(series => {
                const checkboxId = `chart-series-${series.id}`;
                return (
                  <div className="checkbox" key={series.id}>
                    <input
                      id={checkboxId}
                      type="checkbox"
                      checked={
                        this.state.draftSeriesIds.indexOf(series.id) >= 0
                      }
                      onChange={() => this.toggleDraftSeries(series.id)}
                    />
                    <label htmlFor={checkboxId}>{series.label}</label>
                  </div>
                );
              })}
              {availableSeries.length === 0 && (
                <span>
                  To urządzenie nie ma wejść udostępnionych na wykresach.
                </span>
              )}
            </div>
            <span className="help-block">
              Możesz wybrać kilka serii. Wszystkie używają wspólnej osi czasu.
            </span>
          </FormGroup>

          <FormGroup>
            <ControlLabel>Zakres danych</ControlLabel>
            <FormControl
              componentClass="select"
              value={
                this.state.draftRangeMode === "custom"
                  ? "custom"
                  : String(this.state.draftTimeRange)
              }
              onChange={event => {
                const value = event.target.value;
                this.setState(
                  value === "custom"
                    ? { draftRangeMode: "custom" }
                    : {
                        draftRangeMode: "preset",
                        draftTimeRange: Number(value)
                      }
                );
              }}
            >
              {TIME_RANGES.map(range => (
                <option value={range.value} key={range.value}>
                  {range.label}
                </option>
              ))}
              <option value="custom">Własny zakres</option>
            </FormControl>
          </FormGroup>

          {this.state.draftRangeMode === "custom" && (
            <Row>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Początek zakresu</ControlLabel>
                  <FormControl
                    type="datetime-local"
                    value={this.state.draftCustomFrom}
                    onChange={event =>
                      this.setState({
                        draftCustomFrom: event.target.value,
                        validationError: ""
                      })
                    }
                  />
                </FormGroup>
              </Col>
              <Col sm={6}>
                <FormGroup>
                  <ControlLabel>Koniec zakresu</ControlLabel>
                  <FormControl
                    type="datetime-local"
                    value={this.state.draftCustomTo}
                    onChange={event =>
                      this.setState({
                        draftCustomTo: event.target.value,
                        validationError: ""
                      })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col sm={7}>
              <FormGroup>
                <ControlLabel>Rodzaj wykresu</ControlLabel>
                <FormControl
                  componentClass="select"
                  value={this.state.draftVisualization}
                  onChange={event =>
                    this.setState({
                      draftVisualization: event.target.value
                    })
                  }
                >
                  <option value="line">Liniowy</option>
                  <option value="column">Słupkowy</option>
                </FormControl>
              </FormGroup>
            </Col>
            <Col sm={5}>
              <ControlLabel>Prezentacja</ControlLabel>
              <div className="checkbox">
                <input
                  id="chart-show-points"
                  type="checkbox"
                  checked={this.state.draftShowPoints}
                  disabled={this.state.draftVisualization === "column"}
                  onChange={event =>
                    this.setState({
                      draftShowPoints: event.target.checked
                    })
                  }
                />
                <label htmlFor="chart-show-points">Pokaż punkty</label>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => this.setState({ showModal: false })}>
            Anuluj
          </Button>
          <Button bsStyle="primary" onClick={this.applySettings}>
            Zastosuj
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderCollectionEditModal() {
    const form = this.state.editingCollection;
    if (!form) return null;
    const collectionSeries = this.getCollectionSeries(
      this.state.activeDevice
    );
    return (
      <Modal
        show
        onHide={() => this.setState({ editingCollection: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edytuj zapisany odbiór</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col sm={6}>
              <FormGroup>
                <ControlLabel>Pomiar medium</ControlLabel>
                <FormControl
                  componentClass="select"
                  value={form.inputId}
                  onChange={event =>
                    this.updateEditingCollection(
                      "inputId",
                      event.target.value
                    )
                  }
                >
                  {collectionSeries.map(series => (
                    <option value={series.id} key={series.id}>
                      {series.label}
                    </option>
                  ))}
                </FormControl>
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup>
                <ControlLabel>Data i godzina</ControlLabel>
                <FormControl
                  type="datetime-local"
                  value={form.startedAt}
                  onChange={event =>
                    this.updateEditingCollection(
                      "startedAt",
                      event.target.value
                    )
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FormGroup>
                <ControlLabel>Ilość odebranego medium</ControlLabel>
                <FormControl
                  type="number"
                  min="0.001"
                  step="0.1"
                  value={form.amount}
                  onChange={event =>
                    this.updateEditingCollection("amount", event.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup>
                <ControlLabel>Temperatura [°C]</ControlLabel>
                <FormControl
                  type="number"
                  step="0.1"
                  value={form.temperature}
                  onChange={event =>
                    this.updateEditingCollection(
                      "temperature",
                      event.target.value
                    )
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <ControlLabel>Notatka</ControlLabel>
            <FormControl
              value={form.note}
              maxLength={512}
              onChange={event =>
                this.updateEditingCollection("note", event.target.value)
              }
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => this.setState({ editingCollection: null })}
            disabled={this.state.collectionLoading}
          >
            Anuluj
          </Button>
          <Button
            bsStyle="primary"
            onClick={this.saveEditingCollection}
            disabled={this.state.collectionLoading}
          >
            Zapisz zmiany
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderCollectionPanel() {
    const collectionSeries = this.getCollectionSeries(
      this.state.activeDevice
    );
    const parameterFields = [
      {
        key: "windowMinutes",
        label: "Okno analizy [min]",
        min: 5,
        max: 360,
        step: 5
      },
      {
        key: "minimumDrop",
        label: "Minimalny spadek",
        min: 1,
        max: 1000000,
        step: 10
      },
      {
        key: "smoothingPoints",
        label: "Wygładzanie [punkty]",
        min: 1,
        max: 15,
        step: 2
      },
      {
        key: "maximumGapMinutes",
        label: "Maks. przerwa danych [min]",
        min: 1,
        max: 180,
        step: 1
      },
      {
        key: "settleMinutes",
        label: "Szukanie minimum [min]",
        min: 0,
        max: 120,
        step: 5
      },
      {
        key: "recoveryTolerance",
        label: "Tolerancja odbicia",
        min: 0,
        max: 10000,
        step: 5
      },
      {
        key: "cooldownMinutes",
        label: "Odstęp odbiorów [min]",
        min: 0,
        max: 1440,
        step: 5
      }
    ];

    if (collectionSeries.length === 0)
      return (
        <Card
          title="Odbiory medium"
          category="Brak wejścia umożliwiającego rejestrację odbiorów"
          content={
            <Alert bsStyle="info">
              W ustawieniach urządzenia przypisz wejściu funkcję pomiaru poziomu
              medium.
            </Alert>
          }
        />
      );

    return (
      <Card
        title="Odbiory medium"
        category="Ręczne zapisywanie i testowe wykrywanie odbiorów z danych wykresu"
        content={
          <div>
            {this.state.collectionError && (
              <ViewportAlert
                bsStyle="danger"
                onDismiss={() => this.setState({ collectionError: "" })}
              >
                {this.state.collectionError}
              </ViewportAlert>
            )}
            {this.state.collectionMessage && (
              <ViewportAlert
                bsStyle="success"
                onDismiss={() => this.setState({ collectionMessage: "" })}
              >
                {this.state.collectionMessage}
              </ViewportAlert>
            )}

            <h5>Dodaj odbiór ręcznie</h5>
            <Row>
              <Col sm={3}>
                <FormGroup>
                  <ControlLabel>Pomiar medium</ControlLabel>
                  <FormControl
                    componentClass="select"
                    value={this.state.manualCollection.inputId}
                    onChange={event =>
                      this.updateManualCollection(
                        "inputId",
                        event.target.value
                      )
                    }
                  >
                    {collectionSeries.map(series => (
                      <option value={series.id} key={series.id}>
                        {series.label}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>
              </Col>
              <Col sm={3}>
                <FormGroup>
                  <ControlLabel>Data i godzina rozpoczęcia</ControlLabel>
                  <FormControl
                    type="datetime-local"
                    value={this.state.manualCollection.startedAt}
                    onChange={event =>
                      this.updateManualCollection(
                        "startedAt",
                        event.target.value
                      )
                    }
                  />
                </FormGroup>
              </Col>
              <Col sm={3}>
                <FormGroup>
                  <ControlLabel>Ilość odebranego medium</ControlLabel>
                  <FormControl
                    type="number"
                    min="0.001"
                    step="0.1"
                    value={this.state.manualCollection.amount}
                    onChange={event =>
                      this.updateManualCollection("amount", event.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col sm={3}>
                <FormGroup>
                  <ControlLabel>Temperatura [°C]</ControlLabel>
                  <FormControl
                    type="number"
                    step="0.1"
                    value={this.state.manualCollection.temperature}
                    onChange={event =>
                      this.updateManualCollection(
                        "temperature",
                        event.target.value
                      )
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={9}>
                <FormGroup>
                  <ControlLabel>Notatka</ControlLabel>
                  <FormControl
                    value={this.state.manualCollection.note}
                    maxLength={512}
                    onChange={event =>
                      this.updateManualCollection("note", event.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col sm={3}>
                <Button
                  bsStyle="primary"
                  block
                  style={{ marginTop: 25 }}
                  disabled={this.state.collectionLoading}
                  onClick={this.saveManualCollection}
                >
                  Zapisz odbiór
                </Button>
              </Col>
            </Row>

            <hr />
            <h5>Testowe wykrywanie odbiorów</h5>
            <p className="text-muted">
              Analiza korzysta z danych aktualnie pobranych do wykresu.
              Pomarańczowe obszary oznaczają kandydatów, a zielone zapisane
              odbiory.
            </p>
            <Row>
              {parameterFields.map(field => (
                <Col sm={3} key={field.key}>
                  <FormGroup>
                    <ControlLabel>{field.label}</ControlLabel>
                    <FormControl
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={this.state.detectionParameters[field.key]}
                      onChange={event =>
                        this.updateDetectionParameter(
                          field.key,
                          event.target.value
                        )
                      }
                    />
                  </FormGroup>
                </Col>
              ))}
              <Col sm={3}>
                <Button
                  bsStyle="warning"
                  block
                  style={{ marginTop: 25 }}
                  onClick={this.analyzeCollections}
                >
                  Przeanalizuj wykres
                </Button>
              </Col>
            </Row>

            {this.state.candidates.length > 0 && (
              <div>
                <h5>Wykryte odbiory do zatwierdzenia</h5>
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Rozpoczęcie</th>
                        <th>Zakończenie</th>
                        <th>Wejście</th>
                        <th>Ilość</th>
                        <th>Temperatura</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {this.state.candidates.map(candidate => (
                        <tr key={candidate.key}>
                          <td>
                            {this.formatCollectionDate(candidate.startedAt)}
                          </td>
                          <td>
                            {this.formatCollectionDate(candidate.endedAt)}
                          </td>
                          <td>{candidate.inputLabel}</td>
                          <td>{candidate.amount.toFixed(1)}</td>
                          <td>
                            {candidate.temperature === null
                              ? "—"
                              : `${candidate.temperature.toFixed(1)} °C`}
                          </td>
                          <td>
                            <Button
                              bsStyle="success"
                              bsSize="small"
                              disabled={this.state.collectionLoading}
                              onClick={() =>
                                this.saveDetectedCollection(candidate)
                              }
                            >
                              Dodaj do bazy
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            <h5>Zapisane odbiory w wybranym okresie</h5>
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Data i godzina</th>
                    <th>Wejście</th>
                    <th>Ilość</th>
                    <th>Temperatura</th>
                    <th>Źródło</th>
                    <th>Notatka</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {this.state.collections.map(collection => {
                    const series = collectionSeries.find(
                      item =>
                        Number(item.id) === Number(collection.input_id)
                    );
                    return (
                      <tr key={collection.id}>
                        <td>
                          {this.formatCollectionDate(collection.started_at)}
                        </td>
                        <td>{series ? series.label : collection.input_id}</td>
                        <td>{Number(collection.amount).toFixed(1)}</td>
                        <td>
                          {collection.temperature === null
                            ? "—"
                            : `${Number(collection.temperature).toFixed(
                                1
                              )} °C`}
                        </td>
                        <td>
                          {collection.source === "manual"
                            ? "Ręczny"
                            : "Wykryty"}
                        </td>
                        <td>{collection.note || "—"}</td>
                        <td>
                          <Button
                            bsStyle="info"
                            bsSize="small"
                            disabled={this.state.collectionLoading}
                            onClick={() => this.editCollection(collection)}
                            style={{ marginRight: 6 }}
                          >
                            Edytuj
                          </Button>
                          <Button
                            bsStyle="danger"
                            bsSize="small"
                            disabled={this.state.collectionLoading}
                            onClick={() => this.deleteCollection(collection)}
                          >
                            Usuń
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {this.state.collections.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Brak zapisanych odbiorów.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        }
      />
    );
  }

  render() {
    const devices = Array.isArray(this.props.device) ? this.props.device : [];
    const activeSeries = getAvailableSeries(
      this.props,
      this.state.activeDevice
    ).filter(
      series => this.state.activeSeriesIds.indexOf(series.id) >= 0
    );

    if (devices.length === 0) {
      return (
        <div className="content">
          <Container fluid>
            <Card
              title="Wykresy"
              category="Brak urządzeń przypisanych do konta"
              content={<Alert bsStyle="info">Brak danych do wyświetlenia.</Alert>}
            />
          </Container>
        </div>
      );
    }

    return (
      <div className="content">
        <Container fluid>
          {this.renderSettingsModal(devices)}
          {this.renderCollectionEditModal()}
          <Row>
            <Col md={12}>
              <Card
                title={this.getActiveDeviceName()}
                category={this.getCategory()}
                content={
                  <div>
                    <div className="text-center" style={{ marginBottom: 10 }}>
                      <Button bsStyle="info" onClick={this.openSettings}>
                        Ustawienia wykresu
                      </Button>
                      <Button
                        bsStyle="success"
                        onClick={this.refreshChartData}
                        disabled={this.state.chartLoading}
                        style={{ marginLeft: 8 }}
                      >
                        {this.state.chartLoading ? "Odświeżanie…" : "Odśwież"}
                      </Button>
                      <Button
                        bsStyle="default"
                        onClick={this.exportChartPdf}
                        disabled={this.state.pdfExporting}
                        style={{ marginLeft: 8 }}
                      >
                        {this.state.pdfExporting
                          ? "Tworzenie PDF…"
                          : "Pobierz wykres PDF"}
                      </Button>
                      <div
                        className="text-muted"
                        style={{ marginTop: 8, fontSize: 12 }}
                      >
                        Przesuwaj wykres palcem lub myszą. Powiększaj gestem
                        dwóch palców albo rolką myszy.
                      </div>
                    </div>
                    {this.state.alarmError && (
                      <Alert bsStyle="warning">
                        Nie udało się pobrać oznaczeń alarmów:{" "}
                        {this.state.alarmError}
                      </Alert>
                    )}
                    {this.state.alarmLoading && (
                      <div className="text-muted" style={{ marginBottom: 8 }}>
                        Pobieranie oznaczeń alarmów…
                      </div>
                    )}
                    {this.state.alarmLimitReached && (
                      <Alert bsStyle="warning">
                        Pokazano pierwsze 500 alarmów z wybranego okresu. Zawęź
                        zakres czasu, aby zobaczyć komplet oznaczeń.
                      </Alert>
                    )}
                    <Chart
                      ref={component => {
                        this.chartComponent = component;
                      }}
                      activeTimeButton={this.state.activeTimeRange}
                      rangeFrom={
                        this.state.activeRangeMode === "custom"
                          ? Math.floor(
                              new Date(
                                this.state.activeCustomFrom
                              ).getTime() / 1000
                            )
                          : null
                      }
                      rangeTo={
                        this.state.activeRangeMode === "custom"
                          ? Math.floor(
                              new Date(this.state.activeCustomTo).getTime() /
                                1000
                            )
                          : null
                      }
                      activeDevice={this.state.activeDevice}
                      series={activeSeries}
                      visualization={this.state.activeVisualization}
                      showPoints={this.state.activeShowPoints}
                      sharedTemperatureAxis={
                        this.state.sharedTemperatureAxis &&
                        activeSeries.filter(
                          series => series.functionType === 1
                        ).length > 1
                      }
                      refreshKey={this.state.chartRefreshKey}
                      highlightRanges={this.getHighlightRanges()}
                      alarmMarkers={this.getAlarmMarkers()}
                      onLoadingChange={chartLoading =>
                        this.setState({ chartLoading })
                      }
                      onAlarmSelect={alarmId =>
                        this.setState({ focusedAlarmId: alarmId })
                      }
                      onDataLoaded={this.handleChartDataLoaded}
                      exportTitle={`${this.getActiveDeviceName()} — ${this.getCategory()}`}
                      exportFileName={`wykres-${this.state.activeDevice}`}
                      onHighlightAction={highlight => {
                        const candidate = this.state.candidates.find(
                          item => item.key === highlight.actionKey
                        );
                        if (candidate)
                          this.saveDetectedCollection(candidate);
                      }}
                    />
                    <div
                      className="checkbox text-center"
                      style={{ marginTop: 8 }}
                    >
                      <input
                        id="chart-shared-temperature-axis"
                        type="checkbox"
                        checked={
                          this.state.sharedTemperatureAxis &&
                          activeSeries.filter(
                            series => series.functionType === 1
                          ).length > 1
                        }
                        disabled={
                          activeSeries.filter(
                            series => series.functionType === 1
                          ).length < 2
                        }
                        onChange={event =>
                          this.setState({
                            sharedTemperatureAxis: event.target.checked
                          })
                        }
                      />
                      <label htmlFor="chart-shared-temperature-axis">
                        Wspólna oś danych (pomiary temperatury)
                      </label>
                    </div>
                  </div>
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>{this.renderCollectionPanel()}</Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default Charts;



// WEBPACK FOOTER //
// ./src/views/Charts/Charts.jsx